import { Stock } from './StockTypes';
import type { UserUuid } from '../identity';


export interface ProductImage {
	id: number;
	product_id: number;
	file_id: string;
	position: number;
	is_primary: boolean;
	shopify_image_id?: number;
	alt_text?: string;
	metadata?: {
		original_filename?: string;
		size?: number;
		mime_type?: string;
		width?: number;
		height?: number;
	};
	url: string;
	public_url: string;
	created_at: string;
	updated_at: string;
}

export interface SupplementCustomFields {
	display_name?: string;
	mechanism_of_action?: string;
	dosage?: string;
	link?: string;
	genetic_markers?: string[];
	form?: string;
	indications?: string[];
	contraindications?: string[];
	manufacturing_notes?: string;
	interactions?: string[];
	is_active?: boolean;
	timing_preference?: string;
	frequency?: string;
	brand?: string;
	vendor?: string;
	price?: number;
	wholesale_price?: number;
	is_vegan?: boolean;
	sex?: "male" | "female" | "unisex";
}

export interface Product {
	product_id: number;
	product_shopify_id?: number;
	product_category_id?: number;
	company_id: number;
	custom_fields?: Record<string, any> | SupplementCustomFields;
	images?: ProductImage[];
	name: string;
	description?: string;
	sku: string;
	barcode?: string;
	unit_price?: number;
	unit_cost?: number;
	shopify_variant_id?: number;
	shopify_inventory_item_id?: number;
	sync_to_shopify?: boolean;
	last_shopify_sync_at?: string;
	shopify_handle?: string;
	created_at?: string;
	updated_at?: string;
	status?: string;
	category?: ProductCategory;
	variants?: ProductVariant[];
	stock?: Stock[];
}

export const defaultProduct: Product = {
	product_id: 0,
	company_id: 0,
	name: '',
	sku: '',
};

// Default values for empty custom fields
	export const defaultSupplementFields: SupplementCustomFields = {
		display_name: '',
		mechanism_of_action: '',
		dosage: '',
		link: '',
		genetic_markers: [],
		form: '',
		indications: [],
		contraindications: [],
		manufacturing_notes: '',
		interactions: [],
		is_active: true,
		timing_preference: '',
		frequency: '',
		brand: '',
		vendor: '',
		price: 0,
		wholesale_price: 0,
		is_vegan: false,
		sex: 'unisex'
	};


	export interface ProductCategory {
		id: number;
		name: string;
		description?: string;
		is_active: boolean;
		is_global?: boolean;
		company_id?: number;
		category_type?: 'commerce' | 'inventory';
		created_at?: string;
		updated_at?: string;
	}



	/////////////////////////////// Product merging
	
	export interface ProductMergeRequest {
		id: number;
		shop_domain: string;
		shopify_product_id: number;
		shopify_product_data: Record<string, any>;
		similar_products: Record<number, number>;
		similar_products_details?: Array<{
			product: Product;
			similarity_score: number;
		}>;
		status: "pending" | "approved" | "rejected" | "processed";
		selected_product_id?: number;
		merge_direction?: "shopify_overwrites" | "local_overwrites" | "selective";
		selective_merge_fields?: string[];
		requested_by_user_id?: UserUuid;
		approved_by_user_id?: UserUuid;
		requested_at: string;
		responded_at?: string;
		resulting_product_id?: number;
		processing_notes?: string;
		selectedProduct?: Product;
		resultingProduct?: Product;
		created_at: string;
		updated_at: string;
	}
	
	export interface ProductMergeRequestListParams {
		status?: "pending" | "approved" | "rejected" | "processed";
		per_page?: number;
		page?: number;
	}
	
	export interface ProductMergeRequestApproveParams {
		user_id: UserUuid;
		selected_product_id: number;
		merge_direction: "shopify_overwrites" | "local_overwrites" | "selective";
		selective_merge_fields?: string[];
	}
	
	export interface ProductMergeRequestRejectParams {
		user_id: UserUuid;
		reason?: string;
	}
	
	export interface BulkApproveRequest {
		merge_request_id: number;
		selected_product_id: number;
		merge_direction: "shopify_overwrites" | "local_overwrites" | "selective";
		selective_merge_fields?: string[];
	}
	
	export interface BulkApproveParams {
		user_id: UserUuid;
		requests: BulkApproveRequest[];
	}
	
	export interface MergeRequestStatistics {
		pending: number;
		approved: number;
		rejected: number;
		processed: number;
		total: number;
	}

	
	// ─── Product BVariant ────────────────────────────────────────────────────────────────────
	
	export interface ProductVariant {
			id: number;
			product_id: number;
			shopify_variant_id?: number;
			shopify_inventory_item_id?: number;
			title?: string;
			sku?: string;
			barcode?: string;
			price?: number;
			compare_at_price?: number;
			cost?: number;
			option1?: string;
			option2?: string;
			option3?: string;
			inventory_quantity: number;
			inventory_management?: string;
			inventory_policy?: string;
			is_active: boolean;
			/** True when this is the auto-generated "Default Title" placeholder (no custom options). */
			is_default_variant: boolean;
			position: number;
			weight?: number;
			weight_unit?: string;
			shopify_variant_data?: Record<string, any>;
			formatted_title?: string;
			/** Shopify "available" — units purchasable. */
			total_stock?: number;
			/** Shopify "committed" — ordered, not yet shipped. */
			committed_stock?: number;
			/** Physical on-hand = total_stock + committed_stock. */
			on_hand_stock?: number;
			/** @deprecated Use committed_stock */
			reserved_stock?: number;
			/** @deprecated Use committed_stock */
			assigned_stock?: number;
			stock?: Stock[];
			created_at: string;
			updated_at: string;
	}
	
	export interface CreateVariantParams {
			product_id: number;
			title?: string;
			sku: string;
			barcode?: string;
			price: number;
			compare_at_price?: number;
			cost?: number;
			option1?: string;
			option2?: string;
			option3?: string;
			weight?: number;
			weight_unit?: 'kg' | 'g' | 'lb' | 'oz';
			inventory_management?: string;
			inventory_policy?: 'deny' | 'continue';
			is_active?: boolean;
	}
	
	export type UpdateVariantParams = Omit<Partial<CreateVariantParams>, 'product_id'>;